/**
 * ShoppingListView — pickup-vendor counterpart of the Orders page. Warehouse
 * runs (Costco, Restaurant Depot, …) produce shopping lists instead of POs;
 * this view is the phone-in-hand trip UI.
 *
 * Two modes:
 *   overview — open/completed lists (vendor, item count, status, created date)
 *   detail   — planned-list editing on the desk, or per-item found/unavailable,
 *              actual qty/price, "move to another vendor", and trip completion.
 *
 * Props:
 *   storeId - current store id
 *   userId? - recorded as completed_by; falls back to localStorage currentUser
 *   onEditVendor? - opens the linked vendor in the Ordering vendor editor
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  addShoppingListItem,
  fetchShoppingListActivity,
  fetchShoppingListPLCategories,
  fetchShoppingListVendorCatalog,
  fetchShoppingLists,
  fetchShoppingList,
  fetchSubstituteCandidates,
  fetchTripPurchase,
  reconcileTripReceipt,
  markItem,
  markNotPurchased,
  moveItemToOption,
  recordEnteredPrice,
  recordSubstitution,
  suggestOngoingSubstitute,
  removeShoppingListItem,
  reopenTrip,
  updateShoppingListDetails,
  updateShoppingListCharges,
  updateShoppingListItemPlan,
  completeTrip,
} from '../services/shoppingListService';
import { fetchOptionsForItems } from '../services/purchaseOptionService';
import { listAllocatableOptions } from '../utils/purchaseOptions';
import {
  buildComparablePriceDisplay,
  comparableFromSnapshot,
  purchaseUnitLabel,
} from '../utils/priceComparison';
import {
  buildShoppingFinancials,
  formatShoppingDateTime,
  groupShoppingListItems,
  shoppingListStats,
} from '../utils/shoppingListExperience';
import {
  NOT_PURCHASED_REASONS,
  buildTripReview,
  isPurchase,
  notPurchasedReasonLabel,
  outcomeOf,
  priceEvidence,
  reconciliationLabel,
  substituteLabel,
  summarizeReconciliation,
} from '../utils/shoppingTripOutcome';

const money = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : '—';
};

const toNullableNumber = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const STATUS_PILLS = {
  open: { bg: '#E3F2FD', fg: '#0d47a1', label: 'Open' },
  completed: { bg: '#E8F5E9', fg: '#2E7D32', label: 'Completed' },
  canceled: { bg: '#F5F5F5', fg: '#757575', label: 'Canceled' },
};

const styles = {
  wrap: { fontFamily: 'Open Sans, sans-serif', color: '#3D2518', fontSize: 14, maxWidth: 1280, margin: '0 auto' },
  muted: { color: '#8B7B73', fontSize: 13 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 6px' },
  listCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: 16,
    width: '100%', textAlign: 'left', background: '#fff', border: '1.5px solid #E9DFD6',
    borderRadius: 12, padding: '14px 16px', minHeight: 108, cursor: 'pointer', fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  listVendor: { fontWeight: 700, fontSize: 15, color: '#3D2518' },
  pill: (colors) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 999, background: colors.bg,
    color: colors.fg, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
  }),
  itemCard: (dimmed) => ({
    background: 'rgba(255,255,255,.76)', border: '1px solid #E8DCCB', borderRadius: 9, padding: '12px 14px',
    minHeight: 92, position: 'relative', overflow: 'hidden', opacity: dimmed ? 0.68 : 1,
  }),
  itemTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  itemName: { fontWeight: 700, fontSize: 15, color: '#3D2518' },
  itemMeta: { color: '#6B5A52', fontSize: 12, marginTop: 2 },
  itemPrice: { textAlign: 'right', fontSize: 13, color: '#5A4A42', whiteSpace: 'nowrap', minWidth: 122, fontVariantNumeric: 'tabular-nums' },
  comparable: { fontSize: 12, color: '#8B7B73' },
  foundRow: { display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' },
  foundBtn: (active, colors) => ({
    flex: 1, minHeight: 38, padding: '8px 12px', borderRadius: 9, fontWeight: 700, fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
    background: active ? colors.bg : '#fff',
    color: active ? colors.fg : '#5A4A42',
    border: `1.5px solid ${active ? colors.fg : '#d5c9c0'}`,
  }),
  moveBtn: {
    minHeight: 38, padding: '8px 12px', borderRadius: 9, fontWeight: 600, fontSize: 12,
    cursor: 'pointer', fontFamily: 'inherit', background: '#fff', color: '#5A4A42',
    border: '1.5px solid #d5c9c0', whiteSpace: 'nowrap',
  },
  actualsRow: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  actualField: { flex: 1, minWidth: 110, display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: '#5A4A42', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 },
  input: {
    padding: '9px 10px', border: '1.5px solid #E9DFD6', borderRadius: 8, fontSize: 16,
    color: '#3D2518', background: '#fff', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
  },
  popover: {
    marginTop: 8, background: '#FBF8F5', border: '1.5px solid #DCCFC2', borderRadius: 10, padding: 8,
  },
  popoverOption: {
    display: 'block', width: '100%', textAlign: 'left', background: '#fff',
    border: '1px solid #E9DFD6', borderRadius: 8, padding: '8px 10px', marginBottom: 6,
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: '#3D2518',
  },
  // Move-list rows mirror the vendor picker's anatomy: vendor + tags, pack
  // description, then the package price under its real purchase-unit word
  // beside the comparable price in the relationship unit.
  moveTop: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  moveVendor: { fontWeight: 700, fontSize: 12.5, color: '#3D2518', wordBreak: 'break-word' },
  moveTag: (bg, fg) => ({
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
    background: bg, color: fg, borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
  }),
  moveMeta: { display: 'block', fontSize: 11, color: '#8B7B73', marginTop: 3, wordBreak: 'break-word' },
  movePrice: { display: 'block', fontSize: 11.5, color: '#3D2518', fontWeight: 700, marginTop: 2 },
  footer: {
    background: '#fff', borderTop: '1.5px solid #E9DFD6', padding: '12px 0 0', marginTop: 12,
  },
  total: { fontSize: 15, fontWeight: 700, color: '#3D2518' },
  completeBtn: {
    minHeight: 44, padding: '10px 22px', borderRadius: 10, fontWeight: 700, fontSize: 14,
    cursor: 'pointer', fontFamily: 'inherit', background: '#2E7D32', color: '#fff', border: 'none',
  },
  backBtn: {
    background: 'none', border: 'none', color: '#5A4A42', fontWeight: 700, fontSize: 13,
    cursor: 'pointer', padding: '6px 0', fontFamily: 'inherit',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0 14px',
  },
  editBtn: {
    minHeight: 38, padding: '8px 14px', borderRadius: 9, fontWeight: 700, fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit', background: '#5A4A42', color: '#fff', border: '1.5px solid #5A4A42',
  },
  secondaryBtn: {
    minHeight: 38, padding: '8px 14px', borderRadius: 9, fontWeight: 700, fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit', background: '#fff', color: '#5A4A42', border: '1.5px solid #d5c9c0',
  },
  removeBtn: {
    minHeight: 34, padding: '6px 10px', borderRadius: 8, fontWeight: 700, fontSize: 12,
    cursor: 'pointer', fontFamily: 'inherit', background: '#fff', color: '#b71c1c', border: '1.5px solid #ef9a9a',
  },
  editorCard: {
    background: '#FBF8F5', border: '1.5px solid #DCCFC2', borderRadius: 12, padding: 12, marginBottom: 12,
  },
  editorGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
    gap: 8, alignItems: 'end', marginTop: 10,
  },
  searchTray: {
    background: '#fff', border: '1.5px solid #DCCFC2', borderRadius: 12, padding: 12, marginBottom: 12,
  },
  searchResult: {
    display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    textAlign: 'left', background: '#fff', border: '1px solid #E9DFD6', borderRadius: 9,
    padding: '9px 11px', marginTop: 6, cursor: 'pointer', fontFamily: 'inherit', color: '#3D2518',
  },
  saveState: { color: '#6B5A52', fontSize: 12, marginLeft: 'auto' },
  summary: {
    background: '#E8F5E9', border: '1.5px solid #66bb6a', color: '#1b5e20',
    borderRadius: 10, padding: '10px 14px', marginTop: 10, fontSize: 13, fontWeight: 600,
  },
  // ── The trip report ────────────────────────────────────────────────────
  // Phone-first: everything stacks, nothing needs a horizontal scroll, and the
  // numbers that answer "what did this trip cost me" are the largest thing on
  // the card.
  reportCard: {
    background: '#fff', border: '1.5px solid #E9DFD6', borderRadius: 12,
    padding: '14px 16px', marginTop: 10, marginBottom: 14,
  },
  reportTripNo: {
    fontSize: 11, fontWeight: 700, color: '#8B7B73', letterSpacing: '0.06em',
    textTransform: 'uppercase', marginBottom: 8,
  },
  reportCounts: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  countChip: (colors) => ({
    flex: '1 1 92px', minWidth: 92, background: colors.bg, color: colors.fg,
    border: `1px solid ${colors.fg}33`, borderRadius: 10, padding: '8px 10px',
  }),
  countNumber: { fontSize: 20, fontWeight: 700, lineHeight: 1.1 },
  countLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 },
  moneyRow: {
    display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline',
    padding: '6px 0', borderTop: '1px solid #F0E8E0', fontSize: 13,
  },
  moneyLabel: { color: '#6B5A52' },
  moneyValue: { fontWeight: 700, color: '#3D2518', whiteSpace: 'nowrap' },
  outcomePill: (colors) => ({
    display: 'inline-block', padding: '3px 9px', borderRadius: 999, background: colors.bg,
    color: colors.fg, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', whiteSpace: 'nowrap',
  }),
  evidenceTag: (colors) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: 6, background: colors.bg,
    color: colors.fg, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.04em', whiteSpace: 'nowrap',
  }),
  substLine: { fontSize: 12.5, color: '#5A4A42', marginTop: 4, wordBreak: 'break-word' },
  reviewSheet: {
    background: '#FBF8F5', border: '1.5px solid #DCCFC2', borderRadius: 12,
    padding: 14, marginTop: 10, marginBottom: 12,
  },
  reviewGroupTitle: {
    fontSize: 11, fontWeight: 700, color: '#8B7B73', textTransform: 'uppercase',
    letterSpacing: '0.06em', margin: '12px 0 6px',
  },
  reviewRow: {
    display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center',
    background: '#fff', border: '1px solid #E9DFD6', borderRadius: 9,
    padding: '9px 11px', marginBottom: 6, flexWrap: 'wrap',
  },
  warnBox: {
    background: '#FFF4E5', border: '1.5px solid #F0B27A', color: '#7E4A11',
    borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 10,
  },
  reasonRow: { display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' },
  reasonBtn: (active) => ({
    minHeight: 34, padding: '6px 11px', borderRadius: 999, fontWeight: 600, fontSize: 12,
    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
    background: active ? '#5A4A42' : '#fff', color: active ? '#fff' : '#5A4A42',
    border: `1.5px solid ${active ? '#5A4A42' : '#d5c9c0'}`,
  }),
  scopeRow: { display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  error: {
    padding: '10px 14px', color: '#b71c1c', background: '#FDECEA', borderRadius: 10,
    border: '1px solid #f5c2bd', fontSize: 13, marginBottom: 10,
  },
  empty: {
    padding: '32px 16px', textAlign: 'center', color: '#8B7B73', background: '#fff',
    border: '1.5px dashed #E9DFD6', borderRadius: 12, fontSize: 14,
  },
  metadata: {
    display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '5px 12px', margin: '12px 0 0',
    fontSize: 12, color: '#6B5A52',
  },
  sortSelect: {
    width: '100%', padding: '9px 10px', border: '1.5px solid #DCCFC2', borderRadius: 8,
    background: '#fff', color: '#3D2518', fontFamily: 'inherit', fontSize: 13,
  },
};

const shoppingListCss = `
  .shopping-list-overview-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); grid-auto-rows:1fr; gap:10px; }
  .shopping-list-workspace { display:grid; grid-template-columns:minmax(0,1fr) 318px; gap:18px; align-items:start; }
  .shopping-list-paper { min-width:0; padding:18px; border:1px solid #E5D7C5; border-radius:14px; box-shadow:0 6px 22px rgba(61,37,24,.08); background-color:#fffdf7; background-image:repeating-linear-gradient(to bottom, transparent 0, transparent 35px, rgba(96,137,156,.08) 36px); }
  .shopping-list-sidebar { position:sticky; top:14px; border:1.5px solid #E1D5C9; border-radius:14px; background:#fff; padding:15px; box-shadow:0 4px 18px rgba(61,37,24,.06); }
  .shopping-count-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px; margin:10px 0 16px; }
  .shopping-count-tile { min-height:66px; box-sizing:border-box; border-radius:9px; padding:9px; background:#FBF8F5; border:1px solid #EEE4DC; }
  .shopping-count-value { display:block; font:700 20px Montserrat,sans-serif; color:#3D2518; font-variant-numeric:tabular-nums; }
  .shopping-count-label { display:block; margin-top:2px; color:#78675E; font-size:11px; line-height:1.2; }
  .shopping-category-heading { display:flex; align-items:baseline; justify-content:space-between; gap:12px; margin:18px 0 7px; padding:0 2px 6px; border-bottom:1px solid #DCCFC2; color:#3D2518; font:700 13px Montserrat,sans-serif; text-transform:uppercase; letter-spacing:.045em; }
  .shopping-category-heading span { color:#8B7B73; font:600 11px 'Open Sans',sans-serif; letter-spacing:0; text-transform:none; }
  .shopping-item-list { display:grid; gap:7px; }
  .shopping-item-card::after,.shopping-item-card::before { content:''; pointer-events:none; position:absolute; z-index:2; left:9px; right:9px; top:50%; height:2px; opacity:0; transform-origin:center; }
  .shopping-item-card.is-purchased::after { opacity:.52; background:#219653; transform:rotate(-1.2deg); box-shadow:0 1px 0 rgba(255,255,255,.75); }
  .shopping-item-card.is-purchased .shopping-item-name { text-decoration:line-through 2px rgba(33,150,83,.58); text-decoration-skip-ink:none; }
  .shopping-item-card.is-not-purchased::before { opacity:.42; background:#C83D36; transform:rotate(7deg); }
  .shopping-item-card.is-not-purchased::after { opacity:.42; background:#C83D36; transform:rotate(-7deg); }
  .shopping-item-card > * { position:relative; z-index:1; }
  .shopping-status-row { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:6px; margin-top:10px; }
  .shopping-financial-row { display:grid; grid-template-columns:minmax(0,1fr) 94px; align-items:baseline; gap:12px; padding:5px 0; color:#5A4A42; }
  .shopping-financial-row > :last-child { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
  .shopping-financial-row.is-total { margin-top:5px; padding-top:10px; border-top:1.5px solid #DCCFC2; color:#3D2518; font-weight:800; font-size:16px; }
  .shopping-meta-dt { color:#8B7B73; font-weight:600; }
  .shopping-meta-dd { margin:0; color:#5A4A42; }
  @media (max-width:760px) {
    .shopping-list-overview-grid { grid-template-columns:1fr; }
    .shopping-list-workspace { grid-template-columns:minmax(0,1fr) minmax(128px,36vw); gap:8px; }
    .shopping-list-paper { padding:10px; border-radius:10px; }
    .shopping-list-sidebar { top:8px; padding:9px; border-radius:10px; max-height:calc(100vh - 92px); overflow:auto; }
    .shopping-count-grid { grid-template-columns:1fr; gap:5px; }
    .shopping-count-tile { min-height:48px; padding:6px 8px; }
    .shopping-count-value { font-size:17px; }
    .shopping-status-row { grid-template-columns:1fr; }
    .shopping-financial-row { grid-template-columns:minmax(0,1fr) 70px; gap:5px; font-size:11px; }
    .shopping-item-card { padding:10px !important; }
  }
`;

const FOUND_COLORS = { bg: '#E8F5E9', fg: '#2E7D32' };
const UNAVAILABLE_COLORS = { bg: '#FDECEA', fg: '#b71c1c' };
const SUBSTITUTE_COLORS = { bg: '#EDE7F6', fg: '#4527A0' };
const PENDING_COLORS = { bg: '#F5F0EB', fg: '#6B5A52' };

/**
 * One outcome, one word, one colour — everywhere. The old report printed the
 * raw column value ("Status: unavailable") and folded four different things
 * into a single "skipped" count.
 */
const OUTCOME_PRESENTATION = {
  found: { label: 'Purchased', colors: FOUND_COLORS },
  substituted: { label: 'Substituted', colors: SUBSTITUTE_COLORS },
  not_purchased: { label: 'Not purchased', colors: UNAVAILABLE_COLORS },
  pending: { label: 'Not answered', colors: PENDING_COLORS },
};

/**
 * How much a price on a line is worth. This is the distinction the whole
 * feature turns on, so it is never left implicit in the UI: a number carried
 * from the shopping plan is labelled as expected, not as fact.
 */
const EVIDENCE_PRESENTATION = {
  planned_assumed: { label: 'Expected', colors: { bg: '#FFF4E0', fg: '#8A5A00' } },
  shopper_entered: { label: 'You entered', colors: { bg: '#E3F2FD', fg: '#0d47a1' } },
  receipt_verified: { label: 'Receipt verified', colors: FOUND_COLORS },
};
// Same tag colors as the vendor picker, so "lowest" and "estimated" read as
// one vocabulary across surfaces.
const LOWEST_TAG = { bg: '#E8F5E9', fg: '#2E7D32' };
const ESTIMATED_TAG = { bg: '#FFF4E0', fg: '#8A5A00' };

const lineEstimate = (item) => {
  const lineTotal = Number.parseFloat(item?.estimated_line_total);
  if (Number.isFinite(lineTotal)) return lineTotal;
  const packages = Number.parseFloat(item?.packages);
  const price = Number.parseFloat(item?.estimated_price);
  return Number.isFinite(packages) && Number.isFinite(price) ? packages * price : null;
};

// The option the line will actually be bought as: a mid-trip move wins over
// the originally allocated option.
const currentOptionIdOf = (item) => item?.moved_to_purchase_option_id || item?.purchase_option_id || null;

/**
 * The comparable price a line shows: the FROZEN comparison_snapshot (the facts
 * as decided at allocation-confirm time) wins for an unmoved line, because the
 * shopper is buying what was planned and the plan's figures are the truth even
 * if the live option's price has since changed. A moved line points at a
 * DIFFERENT option, so the frozen figure no longer describes the purchase —
 * it falls through to the live view-model row for the moved-to option.
 * Null when neither side has a displayable cost — absent money renders as
 * nothing, never $0.00.
 */
const lineComparable = (item, liveRow) => {
  const frozen = item?.moved_to_purchase_option_id ? null : comparableFromSnapshot(item?.comparison_snapshot);
  if (frozen?.comparableDisplay) return { text: frozen.comparableDisplay, estimated: frozen.estimated };
  if (liveRow?.comparableDisplay) return { text: liveRow.comparableDisplay, estimated: liveRow.estimated };
  return null;
};

export default function ShoppingListView({ storeId, userId, onEditVendor }) {
  const [lists, setLists] = useState(null); // null = loading
  const [listsError, setListsError] = useState('');

  const [activeList, setActiveList] = useState(null); // list row incl. items
  const [activity, setActivity] = useState(null);
  const [pnlMapping, setPnlMapping] = useState({ byInventoryCategory: {}, hasMappings: false });
  const [sortMode, setSortMode] = useState('category');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // "Move to another vendor" popover state (one item at a time).
  const [moveFor, setMoveFor] = useState(null);
  const [moveOptions, setMoveOptions] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);

  // Purchase options per inventory item, fetched once per list open (and
  // refreshed by the move flow). Feeds the live comparable-price fallback and
  // the move list's price display; empty when the fetch fails — the trip UI
  // stands without comparables.
  const [optionsByItemId, setOptionsByItemId] = useState({});

  const [completing, setCompleting] = useState(false);
  const [completion, setCompletion] = useState(null); // { updatedOptionIds, skippedItemIds }
  const [reviewOpen, setReviewOpen] = useState(false);
  // The purchase record a finished trip produced: a store_invoices row and its
  // lines. Null for a trip that bought nothing, which is a real trip.
  const [tripPurchase, setTripPurchase] = useState(null);
  // Substitution: which line is being substituted, and the candidates offered.
  const [substituteFor, setSubstituteFor] = useState(null);
  const [substituteCandidates, setSubstituteCandidates] = useState([]);
  const [substituteLoading, setSubstituteLoading] = useState(false);
  const [substituteDraft, setSubstituteDraft] = useState(null);
  const [reasonFor, setReasonFor] = useState(null);

  // Desk-only planning editor. Trip controls stay visually separate so a
  // planned quantity is never mistaken for what the shopper actually found.
  const [editingPlan, setEditingPlan] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [planSaved, setPlanSaved] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogRows, setCatalogRows] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // One debounce timer per item+field so rapid typing collapses into one write.
  const markTimers = useRef({});
  const planSavedTimer = useRef(null);
  useEffect(() => () => {
    Object.values(markTimers.current).forEach(clearTimeout);
    if (planSavedTimer.current) clearTimeout(planSavedTimer.current);
  }, []);

  const resolvedUserId = userId || (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}')?.id || null; } catch (_) { return null; }
  })();

  const loadLists = useCallback(async () => {
    if (!storeId) {
      setLists([]);
      return;
    }
    try {
      const rows = await fetchShoppingLists(storeId);
      setLists(rows || []);
      setListsError('');
    } catch (err) {
      setLists([]);
      setListsError(err?.message || 'Failed to load shopping lists.');
    }
  }, [storeId]);

  useEffect(() => {
    setLists(null);
    loadLists();
  }, [loadLists]);

  const openList = async (list) => {
    setDetailLoading(true);
    setDetailError('');
    setCompletion(null);
    setMoveFor(null);
    setOptionsByItemId({});
    setEditingPlan(false);
    setShowAddItem(false);
    setCatalogRows([]);
    setActivity(null);
    setPnlMapping({ byInventoryCategory: {}, hasMappings: false });
    setSortMode('category');
    setReviewOpen(false);
    setTripPurchase(null);
    setSubstituteFor(null);
    setSubstituteDraft(null);
    setReasonFor(null);
    try {
      const [full, activityResult, pnlResult] = await Promise.all([
        fetchShoppingList(list.id),
        fetchShoppingListActivity(list.id).catch(() => null),
        fetchShoppingListPLCategories(storeId).catch(() => ({ byInventoryCategory: {}, hasMappings: false })),
      ]);
      if (full) {
        setActiveList(full);
        setActivity(activityResult);
        setPnlMapping(pnlResult);
        // A finished trip's purchase record. Best-effort: a trip that recorded
        // no purchase, or a store still behind on the migration, must still
        // open — the report falls back to the trip's own lines.
        if (full.status === 'completed') {
          try {
            setTripPurchase(await fetchTripPurchase(full.id));
          } catch (purchaseErr) {
            console.warn('[shopping-list] Purchase record unavailable:', purchaseErr?.message);
          }
        }
        // Comparable prices are an enrichment on top of the frozen snapshots —
        // an option-fetch failure must never block the trip UI.
        const itemIds = [...new Set(
          (full.items || []).map((item) => item.inventory_item_id).filter((id) => id != null),
        )];
        if (itemIds.length) {
          try {
            setOptionsByItemId(await fetchOptionsForItems(storeId, itemIds) || {});
          } catch (optionsErr) {
            console.warn('[shopping-list] Options unavailable for comparables:', optionsErr?.message);
          }
        }
      } else {
        setDetailError('Shopping list not found.');
      }
    } catch (err) {
      setDetailError(err?.message || 'Failed to load shopping list.');
    } finally {
      setDetailLoading(false);
    }
  };

  const backToLists = () => {
    setActiveList(null);
    setActivity(null);
    setCompletion(null);
    setMoveFor(null);
    setEditingPlan(false);
    setShowAddItem(false);
    loadLists();
  };

  const patchItemLocal = (itemId, patch) => {
    setActiveList((prev) => (prev ? {
      ...prev,
      items: (prev.items || []).map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
    } : prev));
  };

  const markPlanSaved = () => {
    if (planSavedTimer.current) clearTimeout(planSavedTimer.current);
    setPlanSaved(true);
    planSavedTimer.current = window.setTimeout(() => setPlanSaved(false), 1800);
  };

  const patchPlannedField = (item, field, rawValue) => {
    const value = field === 'notes' ? rawValue : toNullableNumber(rawValue);
    const next = { ...item, [field]: value };
    next.estimated_line_total = lineEstimate({
      ...next,
      estimated_line_total: null,
    });
    patchItemLocal(item.id, {
      [field]: value,
      estimated_line_total: next.estimated_line_total,
    });
  };

  const savePlannedItem = async (itemId) => {
    const item = (activeList?.items || []).find((row) => row.id === itemId);
    if (!item) return;
    setPlanSaving(true);
    setPlanSaved(false);
    try {
      const saved = await updateShoppingListItemPlan(item.id, {
        packages: item.packages,
        estimatedPrice: item.estimated_price,
        notes: item.notes,
      });
      if (saved) patchItemLocal(item.id, saved);
      setDetailError('');
      markPlanSaved();
    } catch (err) {
      setDetailError(err?.message || 'Failed to save the shopping-list item.');
    } finally {
      setPlanSaving(false);
    }
  };

  const saveListNotes = async (rawNotes) => {
    if (!activeList) return;
    setPlanSaving(true);
    setPlanSaved(false);
    try {
      const saved = await updateShoppingListDetails(activeList.id, { notes: rawNotes });
      setActiveList((prev) => (prev ? { ...prev, notes: saved?.notes ?? rawNotes } : prev));
      setDetailError('');
      markPlanSaved();
    } catch (err) {
      setDetailError(err?.message || 'Failed to save the shopping-list notes.');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleChargeChange = (field, rawValue) => {
    if (!activeList || activeList.status === 'completed') return;
    const column = field === 'tax' ? 'tax_amount' : 'other_charges';
    const value = toNullableNumber(rawValue);
    setActiveList((prev) => (prev ? { ...prev, [column]: value } : prev));
    const key = `${activeList.id}:charges`;
    if (markTimers.current[key]) clearTimeout(markTimers.current[key]);
    markTimers.current[key] = setTimeout(async () => {
      try {
        const current = activeList;
        const taxAmount = field === 'tax' ? value : current.tax_amount;
        const otherCharges = field === 'other' ? value : current.other_charges;
        const saved = await updateShoppingListCharges(activeList.id, { taxAmount, otherCharges });
        if (saved) {
          setActiveList((prev) => (prev ? {
            ...prev,
            tax_amount: saved.tax_amount,
            other_charges: saved.other_charges,
            additional_fees: saved.additional_fees,
          } : prev));
        }
        setDetailError('');
      } catch (err) {
        setDetailError(err?.message || 'Failed to save receipt charges.');
      }
    }, 450);
  };

  const removePlannedItem = async (item) => {
    if (!window.confirm(`Remove ${item.description || 'this item'} from this shopping list?`)) return;
    setPlanSaving(true);
    setPlanSaved(false);
    try {
      await removeShoppingListItem(item.id);
      setActiveList((prev) => (prev ? {
        ...prev,
        items: (prev.items || []).filter((row) => row.id !== item.id),
      } : prev));
      setDetailError('');
      markPlanSaved();
    } catch (err) {
      setDetailError(err?.message || 'Failed to remove the shopping-list item.');
    } finally {
      setPlanSaving(false);
    }
  };

  const openAddItem = async () => {
    setShowAddItem(true);
    setCatalogQuery('');
    if (catalogRows.length || !activeList?.vendor_id) return;
    setCatalogLoading(true);
    try {
      setCatalogRows(await fetchShoppingListVendorCatalog(storeId, activeList.vendor_id));
      setDetailError('');
    } catch (err) {
      setDetailError(err?.message || 'Failed to load products for this vendor.');
    } finally {
      setCatalogLoading(false);
    }
  };

  const addPlannedItem = async (catalogRow) => {
    if (!activeList || !catalogRow?.item) return;
    setPlanSaving(true);
    setPlanSaved(false);
    try {
      const nextSortOrder = (activeList.items || []).reduce(
        (max, row) => Math.max(max, Number(row.sort_order) || 0),
        0,
      ) + 1;
      const inserted = await addShoppingListItem(
        activeList,
        catalogRow.item,
        catalogRow,
        { packages: 1, sortOrder: nextSortOrder },
      );
      setActiveList((prev) => (prev ? {
        ...prev,
        items: [...(prev.items || []), {
          ...inserted,
          category_snapshot: inserted.category_snapshot || catalogRow.item.category || null,
        }],
      } : prev));
      setShowAddItem(false);
      setCatalogQuery('');
      setDetailError('');
      markPlanSaved();
    } catch (err) {
      setDetailError(err?.message || 'Failed to add the item to this shopping list.');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleFoundStatus = async (item, foundStatus) => {
    const nextStatus = item.found_status === foundStatus ? 'pending' : foundStatus;
    const previous = item.found_status;
    patchItemLocal(item.id, { found_status: nextStatus });
    try {
      // The database trigger applies the matching quantity/price provenance;
      // this call owns only the shopper's outcome.
      const saved = await markItem(item.id, { foundStatus: nextStatus });
      if (saved) patchItemLocal(item.id, saved);
      fetchShoppingListActivity(activeList.id).then(setActivity).catch(() => {});
      setDetailError('');
    } catch (err) {
      patchItemLocal(item.id, { found_status: previous });
      setDetailError(err?.message || 'Failed to update item.');
    }
  };

  /** "I didn't buy it." The reason is a second, optional question. */
  const handleNotPurchased = async (item) => {
    if (outcomeOf(item) === 'not_purchased') {
      setReasonFor(reasonFor === item.id ? null : item.id);
      return;
    }
    const previous = item.found_status;
    patchItemLocal(item.id, { found_status: 'not_purchased' });
    setReasonFor(item.id);
    try {
      const saved = await markNotPurchased(item.id, null);
      if (saved) patchItemLocal(item.id, saved);
      fetchShoppingListActivity(activeList.id).then(setActivity).catch(() => {});
      setDetailError('');
    } catch (err) {
      patchItemLocal(item.id, { found_status: previous });
      setDetailError(err?.message || 'Failed to update item.');
    }
  };

  const handleNotPurchasedReason = async (item, reason) => {
    patchItemLocal(item.id, { not_purchased_reason: reason });
    setReasonFor(null);
    try {
      await markNotPurchased(item.id, reason);
      setDetailError('');
    } catch (err) {
      setDetailError(err?.message || 'Failed to record the reason.');
    }
  };

  /**
   * Open the substitution tray, offering the products this store could
   * legitimately have bought instead — the Equivalent closure plus one
   * confirmed Alternative hop, via the existing `option_serves_line_item`
   * predicate. This is why "Restaurant Depot is out of Sprite" can now offer
   * the Sysco equivalent: the alternatives live in the relationship layer, not
   * as extra purchase options on the same item, which is why the Move popover
   * was always empty in a one-option-per-item catalog.
   */
  const toggleSubstituteTray = async (item) => {
    if (substituteFor === item.id) {
      setSubstituteFor(null);
      setSubstituteDraft(null);
      return;
    }
    setSubstituteFor(item.id);
    setSubstituteDraft(null);
    setSubstituteCandidates([]);
    setSubstituteLoading(true);
    try {
      const rows = await fetchSubstituteCandidates(storeId, item.inventory_item_id);
      setSubstituteCandidates(
        rows.filter((row) => String(row.id) !== String(item.purchase_option_id)),
      );
    } catch (err) {
      setDetailError(err?.message || 'Failed to load substitute products.');
    } finally {
      setSubstituteLoading(false);
    }
  };

  const handleSaveSubstitution = async (item) => {
    const draft = substituteDraft;
    if (!draft || draft.itemId !== item.id) return;
    try {
      const saved = await recordSubstitution(item.id, {
        purchaseOptionId: draft.purchaseOptionId || null,
        inventoryItemId: draft.inventoryItemId || null,
        description: draft.description || null,
        upc: draft.upc || null,
        vendorSku: draft.vendorSku || null,
        packDescription: draft.packDescription || null,
        scope: draft.scope || 'one_time',
        price: draft.price ?? undefined,
        userId: resolvedUserId || null,
      });
      if (saved) patchItemLocal(item.id, saved);

      // "Use going forward" is a SEPARATE decision, so it makes a separate
      // call — and even then it only PROPOSES. The product joins the
      // related-products review queue as a pending purchasing alternative; it
      // is not merged, not confirmed, and not made preferred.
      if ((draft.scope || 'one_time') === 'ongoing') {
        try {
          const proposal = await suggestOngoingSubstitute(item.id, resolvedUserId);
          if (proposal.created) {
            setDetailError('');
          } else if (proposal.reason === 'substitute_not_in_catalog') {
            // Not a failure: the product simply is not in the catalog yet, and
            // the receipt's new-item pass is what creates it.
            setDetailError('');
          }
        } catch (relateErr) {
          // The substitution itself is recorded. Failing to propose a
          // relationship must never cost the shopper their answer.
          console.warn('[shopping-list] Could not propose the alternative:', relateErr?.message);
        }
      }

      setSubstituteFor(null);
      setSubstituteDraft(null);
      fetchShoppingListActivity(activeList.id).then(setActivity).catch(() => {});
      setDetailError('');
    } catch (err) {
      setDetailError(err?.message || 'Failed to record the substitution.');
    }
  };

  // Debounced actual-quantity / actual-price / notes writes while shopping.
  const handleActualChange = (item, field, rawValue) => {
    const key = `${item.id}:${field}`;
    if (markTimers.current[key]) clearTimeout(markTimers.current[key]);
    const patch = field === 'notes'
        ? { notes: rawValue }
        : field === 'actualQuantity'
          ? { actualQuantity: toNullableNumber(rawValue) }
          : { actualPrice: toNullableNumber(rawValue) };
    const localPatch = field === 'notes'
        ? { notes: rawValue }
        : field === 'actualQuantity'
          ? { actual_quantity: patch.actualQuantity }
          : { actual_price: patch.actualPrice };
    // Totals respond to the keystroke; persistence stays debounced.
    patchItemLocal(item.id, localPatch);
    markTimers.current[key] = setTimeout(async () => {
      try {
        // A price a person types is evidence, and is recorded as such —
        // recordEnteredPrice exists so no call site can write a price without
        // its provenance and leave it looking like the plan's guess.
        const saved = field === 'actualPrice'
          ? await recordEnteredPrice(item.id, rawValue)
          : await markItem(item.id, patch);
        patchItemLocal(item.id, saved || localPatch);
        setDetailError('');
      } catch (err) {
        setDetailError(err?.message || 'Failed to save actuals.');
      }
    }, 400);
  };

  const toggleMovePopover = async (item) => {
    if (moveFor === item.id) {
      setMoveFor(null);
      return;
    }
    setMoveFor(item.id);
    setMoveLoading(true);
    setMoveOptions([]);
    try {
      const grouped = await fetchOptionsForItems(storeId, [item.inventory_item_id]);
      const itemOptions = grouped[item.inventory_item_id] || [];
      // The fresh fetch also refreshes the comparable-price view-model.
      setOptionsByItemId((prev) => ({ ...prev, [item.inventory_item_id]: itemOptions }));
      const currentOptionId = currentOptionIdOf(item);
      const allocatable = listAllocatableOptions(itemOptions)
        .filter((option) => String(option.id) !== String(currentOptionId));
      setMoveOptions(allocatable);
    } catch (err) {
      setDetailError(err?.message || 'Failed to load other vendors.');
    } finally {
      setMoveLoading(false);
    }
  };

  const handleMove = async (item, option) => {
    try {
      await moveItemToOption(item.id, option);
      patchItemLocal(item.id, {
        moved_to_purchase_option_id: option.id,
        vendor_id: option.vendor_id ?? null,
        package_description: option.pack_description || option.purchase_unit || item.package_description,
        estimated_price: Number.parseFloat(option.package_price) || null,
      });
      setMoveFor(null);
      fetchShoppingListActivity(activeList.id).then(setActivity).catch(() => {});
      setDetailError('');
    } catch (err) {
      setDetailError(err?.message || 'Failed to move item.');
    }
  };

  /**
   * Finishing is a two-step: the review names what is about to be recorded,
   * then Finish records it. An untouched line is never quietly turned into a
   * purchase — it is shown as the unanswered question it is.
   */
  const handleFinishTrip = async () => {
    if (!activeList) return;
    setCompleting(true);
    setDetailError('');
    try {
      const result = await completeTrip(activeList.id, resolvedUserId);
      setCompletion(result);
      const completedAt = new Date().toISOString();
      setReviewOpen(false);
      setActiveList((prev) => (prev ? { ...prev, status: 'completed', completed_at: completedAt } : prev));
      fetchShoppingListActivity(activeList.id)
        .then((nextActivity) => setActivity(nextActivity))
        .catch(() => setActivity((prev) => ({ ...prev, completed_at: completedAt })));
      let purchase = await fetchTripPurchase(activeList.id).catch(() => null);
      if (purchase) setTripPurchase(purchase);

      // A receipt scanned before Done has been waiting on this trip: the
      // receipt is bound to the list it was connected to, and finishing is
      // what releases it. Best-effort — the trip is finished either way.
      if (purchase?.source_email_id) {
        const reconciled = await reconcileTripReceipt(activeList.id, storeId).catch(() => null);
        if (reconciled?.reconciled) {
          purchase = await fetchTripPurchase(activeList.id).catch(() => purchase);
          if (purchase) setTripPurchase(purchase);
          const refreshed = await fetchShoppingList(activeList.id).catch(() => null);
          if (refreshed) setActiveList(refreshed);
        }
      }
    } catch (err) {
      setDetailError(err?.message || 'Failed to complete the trip.');
    } finally {
      setCompleting(false);
    }
  };

  const handleReopenTrip = async () => {
    if (!activeList) return;
    if (!window.confirm(
      'Reopen this trip? The purchase it recorded will be removed so you can correct it.\n\n'
      + 'Prices already confirmed against vendor products stay as they are — those were real evidence.',
    )) return;
    setCompleting(true);
    setDetailError('');
    try {
      const result = await reopenTrip(activeList.id, resolvedUserId);
      if (!result.reopened) {
        setDetailError(result.reason === 'receipt_reconciled'
          ? 'This trip has been reconciled against a receipt, so it can no longer be reopened. Correct the receipt instead.'
          : 'This trip could not be reopened.');
        return;
      }
      setCompletion(null);
      setTripPurchase(null);
      setActiveList((prev) => (prev ? { ...prev, status: 'open', trip_number: prev.trip_number } : prev));
    } catch (err) {
      setDetailError(err?.message || 'Failed to reopen the trip.');
    } finally {
      setCompleting(false);
    }
  };

  // ── Overview mode ──────────────────────────────────────────────────────────
  if (!activeList) {
    if (lists === null || detailLoading) {
      return <div style={styles.wrap}><div style={styles.muted}>Loading shopping lists…</div></div>;
    }
    const open = lists.filter((list) => list.status !== 'completed');
    const completed = lists.filter((list) => list.status === 'completed');

    const renderListCard = (list) => {
      const status = STATUS_PILLS[list.status] || { bg: '#F5F5F5', fg: '#5A4A42', label: list.status || 'open' };
      return (
        <button key={list.id} type="button" style={styles.listCard} onClick={() => openList(list)}>
          <div style={{ minWidth: 0, alignSelf: 'center' }}>
            <div style={styles.listVendor}>{list.vendor_name_snapshot || 'Shopping list'}</div>
            <div style={{ ...styles.muted, marginTop: 5 }}>
              {plural(list.item_count ?? 0, 'item')}
            </div>
            {list.created_at && <div style={{ ...styles.muted, marginTop: 2 }}>Created {formatShoppingDateTime(list.created_at)}</div>}
            {list.completed_at && <div style={{ ...styles.muted, marginTop: 2 }}>Shopped {formatShoppingDateTime(list.completed_at)}</div>}
          </div>
          <span style={{ ...styles.pill(status), alignSelf: 'flex-start' }}>{status.label}</span>
        </button>
      );
    };

    return (
      <div style={styles.wrap}>
        <style>{shoppingListCss}</style>
        {listsError && <div style={styles.error} role="alert">{listsError}</div>}
        {detailError && <div style={styles.error} role="alert">{detailError}</div>}
        {lists.length === 0 ? (
          <div style={styles.empty}>
            No shopping lists yet. Lists appear here when a purchase allocation is confirmed for a pickup vendor.
          </div>
        ) : (
          <>
            {open.length > 0 && (
              <>
                <div style={styles.sectionTitle}>Open trips</div>
                <div className="shopping-list-overview-grid">{open.map(renderListCard)}</div>
              </>
            )}
            {completed.length > 0 && (
              <>
                <div style={styles.sectionTitle}>Completed</div>
                <div className="shopping-list-overview-grid">{completed.map(renderListCard)}</div>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Detail mode ────────────────────────────────────────────────────────────
  const items = activeList.items || [];
  const isCompleted = activeList.status === 'completed';
  const estimatedTotal = items.reduce((sum, item) => {
    const estimate = lineEstimate(item);
    return estimate === null ? sum : sum + estimate;
  }, 0);
  const stats = shoppingListStats(items);
  const taxAmount = activeList.tax_amount ?? null;
  const otherCharges = activeList.other_charges ?? activeList.additional_fees ?? null;
  const financials = buildShoppingFinancials(items, {
    taxAmount,
    otherCharges,
    pnlCategoryByInventoryCategory: pnlMapping.byInventoryCategory,
    hasPnlMappings: pnlMapping.hasMappings,
  });
  const tripReview = buildTripReview(items);
  const receiptSummary = tripPurchase
    ? summarizeReconciliation(tripPurchase.lines || [])
    : null;
  const isReconciled = activeList.reconciliation_status === 'reconciled';
  const displayGroups = groupShoppingListItems(items, sortMode);
  const activityModel = {
    created_at: activity?.created_at || activeList.created_at,
    created_by_name: activity?.created_by_name || null,
    first_item_marked_at: activity?.first_item_marked_at || null,
    last_item_marked_at: activity?.last_item_marked_at || null,
    shopper_name: activity?.shopper_name || null,
    completed_at: activity?.completed_at || activeList.completed_at || null,
    substitutions: activity?.substitutions ?? stats.substituted,
  };
  const catalogNeedle = catalogQuery.trim().toLowerCase();
  const existingOptionIds = new Set(items.map((item) => String(currentOptionIdOf(item) || '')).filter(Boolean));
  const visibleCatalogRows = catalogRows.filter((row) => {
    if (existingOptionIds.has(String(row.id))) return false;
    if (!catalogNeedle) return true;
    return [
      row.item?.description,
      row.item?.category,
      row.item?.location,
      row.vendor_description,
      row.vendor_sku,
    ].some((value) => String(value || '').toLowerCase().includes(catalogNeedle));
  });

  const renderItem = (item) => {
    // The same outcome model drives the lifecycle report, item styling,
    // sidebar counts, and receipt reconciliation.
    const outcome = outcomeOf(item);
    const found = outcome === 'found';
    const substituted = outcome === 'substituted';
    const notPurchased = outcome === 'not_purchased';
    const dimmed = notPurchased;
    const price = priceEvidence(item);
    const presentation = OUTCOME_PRESENTATION[outcome];
        const moved = !!item.moved_to_purchase_option_id;

        // One view-model serves the main line's live fallback AND the move
        // list. The list row carries no inventory-item columns, so the
        // relationship unit is inferred from the options' own v2 snapshots.
        const itemOptions = optionsByItemId[item.inventory_item_id] || [];
        const currentOptionId = currentOptionIdOf(item);
        const display = itemOptions.length
          ? buildComparablePriceDisplay({ options: itemOptions, selectedOptionId: currentOptionId })
          : null;
        const displayRowById = new Map((display?.options || []).map((row) => [row.optionId, row]));
        const liveRow = currentOptionId != null ? displayRowById.get(String(currentOptionId)) ?? null : null;
        const comparable = lineComparable(item, liveRow);

        // The package price wears the option's REAL purchase-unit word
        // ('per case', 'per jug'); purchaseUnitLabel falls back to
        // 'per package' only when no option row is on hand to name one.
        // Missing price renders nothing — never $0.00.
        const currentOption = currentOptionId != null
          ? itemOptions.find((option) => String(option.id) === String(currentOptionId)) ?? null
          : null;
        const estimatedPrice = Number.parseFloat(item.estimated_price);
        const packagePriceText = Number.isFinite(estimatedPrice) && estimatedPrice > 0
          ? `${money(estimatedPrice)} ${purchaseUnitLabel(currentOption)}`
          : null;

        // A shopping list is read standing in front of a shelf, so it leads
        // with the VENDOR's own wording — the words on the tag and the invoice.
        // `item.description` is our AI canonical rename ("Whole Milk") and can
        // share no words with what the vendor prints ("WHL MLK GAL 4/1"), so it
        // is demoted to a sub-line and dropped when it says the same thing.
        // Same precedence the phone uses (public/js/shopping-guide.js
        // listLineModel); the two screens must not disagree about a line.
        const vendorWording = String(currentOption?.vendor_description || '').trim();
        const ourWording = String(item.description || '').trim();
        const shelfName = vendorWording || ourWording || 'Item';
        const catalogName = ourWording && ourWording.toLowerCase() !== shelfName.toLowerCase()
          ? ourWording
          : null;

        return (
          <div
            key={item.id}
            style={styles.itemCard(dimmed)}
            className={`shopping-item-card${found || substituted ? ' is-purchased' : ''}${notPurchased ? ' is-not-purchased' : ''}`}
            data-testid="shopping-list-item"
          >
            <div style={styles.itemTop}>
              <div style={{ minWidth: 0 }}>
                <div style={styles.itemName} className="shopping-item-name">{shelfName}</div>
                {catalogName && <div style={styles.itemMeta}>{catalogName}</div>}
                <div style={styles.itemMeta}>
                  {item.package_description || '—'}
                  {item.packages != null && ` · ${plural(item.packages, 'package')}`}
                  {currentOption?.vendor_sku && ` · #${currentOption.vendor_sku}`}
                  {moved && ' · moved to another vendor'}
                  {item.category_snapshot && ` · ${item.category_snapshot}`}
                </div>
              </div>
              <div style={styles.itemPrice}>
                {packagePriceText && <div>{packagePriceText}</div>}
                {comparable && (
                  <div style={styles.comparable}>
                    {comparable.text}
                    {comparable.estimated ? ' · Estimated' : ''}
                  </div>
                )}
                {lineEstimate(item) !== null && <div><strong>{money(lineEstimate(item))}</strong></div>}
              </div>
            </div>

            {editingPlan ? (
              <div style={styles.editorGrid} className="shopping-list-editor-grid">
                <div style={styles.actualField}>
                  <label style={styles.fieldLabel} htmlFor={`planned-packages-${item.id}`}>Planned packages</label>
                  <input
                    id={`planned-packages-${item.id}`}
                    style={styles.input}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    value={item.packages ?? ''}
                    onChange={(e) => patchPlannedField(item, 'packages', e.target.value)}
                    onBlur={() => savePlannedItem(item.id)}
                  />
                </div>
                <div style={styles.actualField}>
                  <label style={styles.fieldLabel} htmlFor={`planned-price-${item.id}`}>Package price</label>
                  <input
                    id={`planned-price-${item.id}`}
                    style={styles.input}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={item.estimated_price ?? ''}
                    onChange={(e) => patchPlannedField(item, 'estimated_price', e.target.value)}
                    onBlur={() => savePlannedItem(item.id)}
                  />
                </div>
                <div style={styles.actualField}>
                  <label style={styles.fieldLabel} htmlFor={`planned-notes-${item.id}`}>Item notes</label>
                  <input
                    id={`planned-notes-${item.id}`}
                    style={styles.input}
                    type="text"
                    value={item.notes ?? ''}
                    placeholder="Brand, substitution, or pickup note"
                    onChange={(e) => patchPlannedField(item, 'notes', e.target.value)}
                    onBlur={() => savePlannedItem(item.id)}
                  />
                </div>
                <button type="button" style={styles.removeBtn} onClick={() => removePlannedItem(item)} disabled={planSaving}>
                  Remove
                </button>
              </div>
            ) : isCompleted ? (
              /*
                What happened to this line, in the customer-facing words. The
                old version printed the raw column ("Status: unavailable") and
                appended the notes field, which is how a substitution the system
                knew nothing about came to look like a recognised one
                ("Status: unavailable · 7up BIB").
              */
              <div data-testid="completed-line">
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                  <span style={styles.outcomePill(presentation.colors)}>{presentation.label}</span>
                  {notPurchased && notPurchasedReasonLabel(item) && (
                    <span style={styles.itemMeta}>{notPurchasedReasonLabel(item)}</span>
                  )}
                  {price.amount !== null && price.source && (
                    <span style={styles.evidenceTag(
                      (EVIDENCE_PRESENTATION[price.source] || EVIDENCE_PRESENTATION.shopper_entered).colors,
                    )}
                    >
                      {(EVIDENCE_PRESENTATION[price.source] || EVIDENCE_PRESENTATION.shopper_entered).label}
                    </span>
                  )}
                  {item.reconciliation_status && (
                    <span style={styles.itemMeta}>{reconciliationLabel(item.reconciliation_status)}</span>
                  )}
                </div>

                {substituted && (
                  <div style={styles.substLine}>
                    <strong>Requested:</strong> {item.description || 'Item'}
                    {' · '}
                    <strong>Bought:</strong> {substituteLabel(item) || 'a different product'}
                    {item.substitute_scope === 'ongoing' ? ' · Use going forward' : ' · One-time substitution'}
                  </div>
                )}

                {isPurchase(item) && (
                  <div style={styles.itemMeta}>
                    {price.quantity != null && `${price.quantity} × `}
                    {price.amount !== null ? money(price.amount) : 'no price recorded'}
                    {price.lineTotal !== null && ` · ${money(price.lineTotal)}`}
                  </div>
                )}
                {item.notes ? <div style={styles.itemMeta}>Note: {item.notes}</div> : null}
              </div>
            ) : (
              <>
                {/*
                  Three answers, not two. "Unavailable" used to be the only way
                  to say "I didn't buy it", which conflated the shop being out
                  with every other reason, and there was no way at all to say
                  "I bought something else" — that went into the notes box and
                  no layer ever saw it.
                */}
                <div style={styles.foundRow}>
                  <button
                    type="button"
                    style={styles.foundBtn(found, FOUND_COLORS)}
                    aria-pressed={found}
                    onClick={() => handleFoundStatus(item, 'found')}
                  >
                    {found ? '✓ Purchased' : 'Purchased'}
                  </button>
                  <button
                    type="button"
                    style={styles.foundBtn(substituted, SUBSTITUTE_COLORS)}
                    aria-pressed={substituted}
                    onClick={() => toggleSubstituteTray(item)}
                    data-testid="substitute-btn"
                  >
                    {substituted ? '⇄ Substituted' : 'Substitute'}
                  </button>
                  <button
                    type="button"
                    style={styles.foundBtn(notPurchased, UNAVAILABLE_COLORS)}
                    aria-pressed={notPurchased}
                    onClick={() => handleNotPurchased(item)}
                    data-testid="not-purchased-btn"
                  >
                    {notPurchased ? '✗ Not purchased' : 'Not purchased'}
                  </button>
                  <button type="button" style={styles.moveBtn} onClick={() => toggleMovePopover(item)}>
                    {moveFor === item.id ? 'Close' : moved ? '✓ Moved vendor' : 'Move vendor'}
                  </button>
                </div>

                {/* Why, asked only once the answer is "no". */}
                {(notPurchased && reasonFor === item.id) && (
                  <div style={styles.reasonRow} data-testid="reason-row">
                    {NOT_PURCHASED_REASONS.map((reason) => (
                      <button
                        key={reason.value}
                        type="button"
                        style={styles.reasonBtn(item.not_purchased_reason === reason.value)}
                        onClick={() => handleNotPurchasedReason(item, reason.value)}
                      >
                        {reason.label}
                      </button>
                    ))}
                  </div>
                )}

                {substituteFor === item.id && (
                  <div style={styles.popover} data-testid="substitute-tray">
                    <div style={{ ...styles.fieldLabel, marginBottom: 6 }}>Bought something else</div>
                    {substituteLoading ? (
                      <div style={styles.muted}>Looking for products that could stand in…</div>
                    ) : (
                      <>
                        {substituteCandidates.length === 0 ? (
                          <div style={styles.muted}>
                            No confirmed stand-in on file. Describe what you bought instead.
                          </div>
                        ) : (
                          substituteCandidates.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              style={styles.popoverOption}
                              data-testid="substitute-candidate"
                              onClick={() => setSubstituteDraft({
                                itemId: item.id,
                                purchaseOptionId: option.id,
                                inventoryItemId: option.inventory_item_id,
                                description: option.item?.description || option.vendor_description || 'Substitute',
                                packDescription: option.pack_description || option.purchase_unit || null,
                                vendorSku: option.vendor_sku || null,
                                upc: option.upc || null,
                                price: option.package_price ?? null,
                                scope: 'one_time',
                              })}
                            >
                              <span style={styles.moveTop}>
                                <span style={styles.moveVendor}>
                                  {option.item?.description || option.vendor_description || 'Product'}
                                </span>
                              </span>
                              <span style={styles.moveMeta}>
                                {[option.vendor?.display_name, option.pack_description || option.purchase_unit]
                                  .filter(Boolean).join(' · ')}
                              </span>
                              {Number.isFinite(Number.parseFloat(option.package_price)) && (
                                <span style={styles.movePrice}>
                                  {money(option.package_price)} {purchaseUnitLabel(option)}
                                </span>
                              )}
                            </button>
                          ))
                        )}

                        <div style={{ ...styles.actualField, marginTop: 8 }}>
                          <label style={styles.fieldLabel} htmlFor={`subst-desc-${item.id}`}>
                            Or type what you bought
                          </label>
                          <input
                            id={`subst-desc-${item.id}`}
                            style={styles.input}
                            type="text"
                            placeholder="e.g. 7UP BIB 5 gal"
                            value={substituteDraft?.itemId === item.id && !substituteDraft.purchaseOptionId
                              ? substituteDraft.description ?? '' : ''}
                            onChange={(e) => setSubstituteDraft({
                              itemId: item.id,
                              purchaseOptionId: null,
                              inventoryItemId: null,
                              description: e.target.value,
                              scope: substituteDraft?.scope || 'one_time',
                            })}
                          />
                        </div>

                        {substituteDraft?.itemId === item.id && (
                          <>
                            {/*
                              Asked separately and explicitly. Buying something
                              once is not a decision about what this kitchen
                              buys from now on, and inferring the second from
                              the first is how a catalog quietly rewrites itself.
                            */}
                            <div style={styles.scopeRow}>
                              <button
                                type="button"
                                style={styles.reasonBtn(substituteDraft.scope === 'one_time')}
                                onClick={() => setSubstituteDraft({ ...substituteDraft, scope: 'one_time' })}
                              >
                                Just this trip
                              </button>
                              <button
                                type="button"
                                style={styles.reasonBtn(substituteDraft.scope === 'ongoing')}
                                onClick={() => setSubstituteDraft({ ...substituteDraft, scope: 'ongoing' })}
                              >
                                Use this going forward
                              </button>
                            </div>
                            <div style={{ ...styles.toolbar, margin: '10px 0 0' }}>
                              <button
                                type="button"
                                style={styles.editBtn}
                                data-testid="substitute-save"
                                onClick={() => handleSaveSubstitution(item)}
                              >
                                Record substitution
                              </button>
                              <button
                                type="button"
                                style={styles.secondaryBtn}
                                onClick={() => { setSubstituteFor(null); setSubstituteDraft(null); }}
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div style={styles.actualsRow}>
                  <div style={styles.actualField}>
                    <label style={styles.fieldLabel} htmlFor={`actual-qty-${item.id}`}>Actual qty</label>
                    <input
                      id={`actual-qty-${item.id}`}
                      style={styles.input}
                      type="number"
                      inputMode="decimal"
                      step="any"
                      min="0"
                      defaultValue={item.actual_quantity ?? ''}
                      onChange={(e) => handleActualChange(item, 'actualQuantity', e.target.value)}
                    />
                  </div>
                  <div style={styles.actualField}>
                    <label style={styles.fieldLabel} htmlFor={`actual-price-${item.id}`}>Actual price / pkg</label>
                    <input
                      id={`actual-price-${item.id}`}
                      style={styles.input}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      defaultValue={item.actual_price ?? ''}
                      onChange={(e) => handleActualChange(item, 'actualPrice', e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ ...styles.actualField, marginTop: 8 }}>
                  <label style={styles.fieldLabel} htmlFor={`notes-${item.id}`}>Notes</label>
                  <input
                    id={`notes-${item.id}`}
                    style={styles.input}
                    type="text"
                    placeholder="Substitution or shopping note"
                    defaultValue={item.notes ?? ''}
                    onChange={(e) => handleActualChange(item, 'notes', e.target.value)}
                  />
                </div>

                {moveFor === item.id && (
                  <div style={styles.popover} data-testid="move-popover">
                    {moveLoading ? (
                      <div style={styles.muted}>Loading other vendors…</div>
                    ) : moveOptions.length === 0 ? (
                      <div style={styles.muted}>No other approved vendors for this item.</div>
                    ) : (
                      moveOptions.map((option) => {
                        const row = displayRowById.get(String(option.id)) ?? null;
                        const packPrice = Number.parseFloat(option.package_price);
                        const packPriceText = Number.isFinite(packPrice) && packPrice > 0
                          ? `${money(packPrice)} ${purchaseUnitLabel(option)}`
                          : 'No package price';
                        return (
                          <button
                            key={option.id}
                            type="button"
                            style={styles.popoverOption}
                            data-testid="move-option"
                            onClick={() => handleMove(item, option)}
                          >
                            <span style={styles.moveTop}>
                              <span style={styles.moveVendor}>{row?.vendorName ?? 'Vendor not set'}</span>
                              {row?.isLowest && (
                                <span style={styles.moveTag(LOWEST_TAG.bg, LOWEST_TAG.fg)}>Lowest comparable price</span>
                              )}
                              {row?.isTiedLowest && (
                                <span style={styles.moveTag(LOWEST_TAG.bg, LOWEST_TAG.fg)}>Tied lowest</span>
                              )}
                              {row?.estimated && (
                                <span style={styles.moveTag(ESTIMATED_TAG.bg, ESTIMATED_TAG.fg)}>Estimated</span>
                              )}
                            </span>
                            {(row?.packDescription ?? option.pack_description) && (
                              <span style={styles.moveMeta}>{row?.packDescription ?? option.pack_description}</span>
                            )}
                            <span style={styles.movePrice}>
                              {packPriceText}
                              {row?.comparableDisplay ? ` · ${row.comparableDisplay}` : ' · comparison unavailable'}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      };

  return (
    <div style={styles.wrap}>
      <style>{shoppingListCss}</style>
      <button type="button" style={styles.backBtn} onClick={backToLists}>← All lists</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, margin: '4px 0 8px' }}>
        <div>
          <div style={{ ...styles.listVendor, fontSize: 20 }}>{activeList.vendor_name_snapshot || 'Shopping list'}</div>
          <dl style={styles.metadata}>
            <dt className="shopping-meta-dt">Created</dt>
            <dd className="shopping-meta-dd">{formatShoppingDateTime(activityModel.created_at) || '—'}</dd>
            <dt className="shopping-meta-dt">Creator</dt>
            <dd className="shopping-meta-dd">{activityModel.created_by_name || '—'}</dd>
            <dt className="shopping-meta-dt">First item marked</dt>
            <dd className="shopping-meta-dd">{formatShoppingDateTime(activityModel.first_item_marked_at) || '—'}</dd>
            <dt className="shopping-meta-dt">Last item marked</dt>
            <dd className="shopping-meta-dd">{formatShoppingDateTime(activityModel.last_item_marked_at) || '—'}</dd>
            <dt className="shopping-meta-dt">Shopped</dt>
            <dd className="shopping-meta-dd">{formatShoppingDateTime(activityModel.completed_at) || 'In progress'}</dd>
            <dt className="shopping-meta-dt">Shopper</dt>
            <dd className="shopping-meta-dd">{activityModel.shopper_name || '—'}</dd>
            <dt className="shopping-meta-dt">Substitutions</dt>
            <dd className="shopping-meta-dd">{activityModel.substitutions}</dd>
          </dl>
        </div>
        <span style={styles.pill(STATUS_PILLS[activeList.status] || STATUS_PILLS.open)}>
          {(STATUS_PILLS[activeList.status] || STATUS_PILLS.open).label}
        </span>
      </div>

      <div style={styles.toolbar}>
        {!isCompleted && (
          <button
            type="button"
            style={editingPlan ? styles.secondaryBtn : styles.editBtn}
            onClick={() => {
              setEditingPlan((value) => !value);
              setShowAddItem(false);
            }}
          >
            {editingPlan ? 'Finish editing' : 'Edit planned list'}
          </button>
        )}
        {activeList.vendor_id && typeof onEditVendor === 'function' && (
          <button type="button" style={styles.secondaryBtn} onClick={() => onEditVendor(activeList.vendor_id)}>
            Vendor details
          </button>
        )}
        <a href={`/shopping-guide.html?list=${encodeURIComponent(activeList.id)}`} style={{ ...styles.secondaryBtn, textDecoration: 'none' }}>
          Open mobile view
        </a>
        {(planSaving || planSaved) && (
          <span style={styles.saveState} role="status">{planSaving ? 'Saving…' : 'Saved'}</span>
        )}
      </div>

      {detailError && <div style={styles.error} role="alert">{detailError}</div>}
      {completion && (
        <div style={styles.summary} role="status">
          Trip complete. Add a receipt when you are ready to verify the purchase.
        </div>
      )}

      <div className="shopping-list-workspace">
        <main className="shopping-list-paper">
          {reviewOpen && !isCompleted && (
            <div style={styles.reviewSheet} data-testid="trip-review">
              <div style={{ fontWeight: 700, fontSize: 16 }}>Trip review</div>
              <div style={styles.itemMeta}>
                {plural(tripReview.summary.total, 'planned item')} · {tripReview.summary.purchased} purchased
                {tripReview.summary.substituted > 0 && ` · ${tripReview.summary.substituted} substituted`}
                {` · ${tripReview.summary.notPurchased} not purchased`}
              </div>
              {tripReview.blockingCount > 0 && (
                <div style={{ ...styles.warnBox, marginTop: 10 }} data-testid="review-warning">
                  <strong>{plural(tripReview.blockingCount, 'item')} still unanswered.</strong>{' '}
                  Finishing records {tripReview.blockingCount === 1 ? 'it' : 'them'} as <strong>not purchased</strong>.
                </div>
              )}
              {tripReview.unresolved.length > 0 && (
                <div>
                  <div style={styles.reviewGroupTitle}>Still to answer · {tripReview.unresolved.length}</div>
                  {tripReview.unresolved.map((row) => (
                    <div key={row.id} style={styles.reviewRow}>
                      <strong>{row.description || 'Item'}</strong>
                      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" style={styles.reasonBtn(false)} onClick={() => handleFoundStatus(row, 'found')}>
                          Purchased
                        </button>
                        <button type="button" style={styles.reasonBtn(false)} onClick={() => handleNotPurchased(row)}>
                          Not purchased
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ ...styles.moneyRow, marginTop: 10 }}>
                <span style={styles.moneyLabel}>Expected</span>
                <span style={styles.moneyValue}>{money(tripReview.summary.expectedTotal)}</span>
              </div>
              <div style={styles.moneyRow}>
                <span style={styles.moneyLabel}>Reported</span>
                <span style={styles.moneyValue}>
                  {tripReview.summary.reportedTotal === null ? 'No prices recorded' : money(tripReview.summary.reportedTotal)}
                </span>
              </div>
              <div style={{ ...styles.toolbar, marginBottom: 0 }}>
                <button type="button" style={styles.completeBtn} disabled={completing} onClick={handleFinishTrip} data-testid="finish-trip">
                  {completing ? 'Finishing…' : 'Finish trip'}
                </button>
                <button type="button" style={styles.secondaryBtn} onClick={() => setReviewOpen(false)}>Keep shopping</button>
              </div>
            </div>
          )}

          {editingPlan && (
            <div style={styles.editorCard} data-testid="shopping-list-editor">
              <div style={{ fontWeight: 700, color: '#3D2518' }}>Edit planned list</div>
              <div style={styles.muted}>Change the plan before shopping. Purchased quantities and prices are recorded on each line.</div>
              <div style={{ ...styles.actualField, marginTop: 10 }}>
                <label style={styles.fieldLabel} htmlFor={`list-notes-${activeList.id}`}>List notes</label>
                <input
                  id={`list-notes-${activeList.id}`}
                  style={styles.input}
                  type="text"
                  defaultValue={activeList.notes ?? ''}
                  placeholder="Instructions for this vendor run"
                  onBlur={(e) => saveListNotes(e.target.value)}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={showAddItem ? () => setShowAddItem(false) : openAddItem}
                  disabled={planSaving || !activeList.vendor_id}
                  title={activeList.vendor_id ? '' : 'Link this list to a vendor before adding products'}
                >
                  {showAddItem ? 'Close item picker' : 'Add item'}
                </button>
              </div>
            </div>
          )}

          {editingPlan && showAddItem && (
            <div style={styles.searchTray} data-testid="shopping-list-item-picker">
              <label style={styles.fieldLabel} htmlFor={`catalog-search-${activeList.id}`}>Items sold by {activeList.vendor_name_snapshot || 'this vendor'}</label>
              <input
                id={`catalog-search-${activeList.id}`}
                style={styles.input}
                type="search"
                value={catalogQuery}
                onChange={(e) => setCatalogQuery(e.target.value)}
                placeholder="Search name, category, location, or item number…"
                autoFocus
              />
              {catalogLoading ? (
                <div style={{ ...styles.muted, marginTop: 8 }}>Loading vendor products…</div>
              ) : visibleCatalogRows.length === 0 ? (
                <div style={{ ...styles.muted, marginTop: 8 }}>
                  {catalogRows.length === 0 ? 'No approved products are linked to this vendor.' : 'No products match this search.'}
                </div>
              ) : visibleCatalogRows.slice(0, 50).map((row) => (
                <button
                  key={row.id}
                  type="button"
                  style={styles.searchResult}
                  onClick={() => addPlannedItem(row)}
                  disabled={planSaving}
                >
                  <span>
                    <strong>{row.item?.description || row.vendor_description || 'Item'}</strong>
                    <span style={{ display: 'block', ...styles.muted }}>
                      {[row.pack_description || row.purchase_unit, row.item?.category, row.vendor_sku ? `#${row.vendor_sku}` : null]
                        .filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {Number.isFinite(Number.parseFloat(row.package_price)) ? `${money(row.package_price)} · Add` : 'Add'}
                  </span>
                </button>
              ))}
            </div>
          )}

          {items.length === 0 && <div style={styles.empty}>This shopping list has no items.</div>}
          {displayGroups.map((group) => (
            <section key={group.label}>
              <div className="shopping-category-heading">
                {group.label}<span>{plural(group.items.length, 'item')}</span>
              </div>
              <div className="shopping-item-list">{group.items.map(renderItem)}</div>
            </section>
          ))}
        </main>

        <aside className="shopping-list-sidebar" aria-label="Shopping list controls and totals">
          <div style={styles.fieldLabel}>Trip progress</div>
          <div className="shopping-count-grid">
            {[
              [stats.total, 'Items'],
              [stats.purchased, 'Purchased'],
              [stats.substituted, 'Substituted'],
              [stats.notPurchased, 'Not purchased'],
            ].map(([value, label]) => (
              <div className="shopping-count-tile" key={label}>
                <span className="shopping-count-value">{value}</span>
                <span className="shopping-count-label">{label}</span>
              </div>
            ))}
          </div>

          <label style={styles.fieldLabel} htmlFor={`shopping-sort-${activeList.id}`}>Sort list</label>
          <select
            id={`shopping-sort-${activeList.id}`}
            style={styles.sortSelect}
            value={sortMode}
            onChange={(event) => setSortMode(event.target.value)}
          >
            <option value="category">Category (store walk)</option>
            <option value="purchased">Purchased first</option>
            <option value="not-purchased">Not purchased first</option>
            <option value="substituted">Substitutions first</option>
          </select>
          <div style={{ ...styles.muted, marginTop: 5 }}>Store layout ordering can be added later.</div>

          <div style={{ ...styles.fieldLabel, marginTop: 18 }}>Financial totals</div>
          {!isCompleted && <div className="shopping-financial-row"><span>Planned</span><span>{money(estimatedTotal)}</span></div>}
          <div className="shopping-financial-row"><strong>Purchases</strong><strong>{money(financials.purchaseTotal)}</strong></div>
          {financials.pnlTotals.map((row) => (
            <div className="shopping-financial-row" key={row.label}>
              <span style={{ paddingLeft: 8 }}>{row.label}</span><span>{money(row.amount)}</span>
            </div>
          ))}

          {isCompleted ? (
            <>
              <div className="shopping-financial-row"><span>Tax</span><span>{money(financials.tax)}</span></div>
              <div className="shopping-financial-row"><span>Other charges</span><span>{money(financials.otherCharges)}</span></div>
            </>
          ) : (
            <>
              <label style={{ ...styles.fieldLabel, marginTop: 8 }} htmlFor={`shopping-tax-${activeList.id}`}>Tax</label>
              <input
                id={`shopping-tax-${activeList.id}`}
                style={{ ...styles.input, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00"
                defaultValue={taxAmount ?? ''}
                onChange={(event) => handleChargeChange('tax', event.target.value)}
              />
              <label style={{ ...styles.fieldLabel, marginTop: 8 }} htmlFor={`shopping-other-${activeList.id}`}>Other charges</label>
              <input
                id={`shopping-other-${activeList.id}`}
                style={{ ...styles.input, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00"
                defaultValue={otherCharges ?? ''}
                onChange={(event) => handleChargeChange('other', event.target.value)}
              />
            </>
          )}
          <div className="shopping-financial-row is-total"><span>Total</span><span>{money(financials.total)}</span></div>

          {isCompleted && (
            <div style={styles.reportCard} data-testid="trip-report" role="status">
              {(activeList.trip_number || completion?.tripNumber) && (
                <div style={styles.reportTripNo}>Shopping trip {activeList.trip_number || completion.tripNumber}</div>
              )}
              {activeList.reconciliation_status === 'needs_review' && (
                <div style={styles.warnBox} data-testid="reconciliation-review">
                  <strong>The receipt does not match the trip in places.</strong> Review the marked lines before relying on the totals.
                </div>
              )}
              <div style={styles.reportCounts}>
                <div style={styles.countChip(FOUND_COLORS)}>
                  <div style={styles.countNumber}>{tripReview.summary.purchased}</div>
                  <div style={styles.countLabel}>Purchased</div>
                </div>
                <div style={styles.countChip(SUBSTITUTE_COLORS)}>
                  <div style={styles.countNumber}>{tripReview.summary.substituted}</div>
                  <div style={styles.countLabel}>Substituted</div>
                </div>
                <div style={styles.countChip(UNAVAILABLE_COLORS)}>
                  <div style={styles.countNumber}>{tripReview.summary.notPurchased}</div>
                  <div style={styles.countLabel}>Not purchased</div>
                </div>
              </div>
              <div style={styles.moneyRow}>
                <span style={styles.moneyLabel}>Expected spend</span>
                <span style={styles.moneyValue}>{money(tripReview.summary.expectedTotal)}</span>
              </div>
              {tripReview.summary.reportedTotal !== null && (
                <div style={styles.moneyRow}>
                  <span style={styles.moneyLabel}>{isReconciled ? 'Receipt spend' : 'Reported spend'}</span>
                  <span style={styles.moneyValue}>{money(tripPurchase?.total_amount ?? tripReview.summary.reportedTotal)}</span>
                </div>
              )}
              {receiptSummary && isReconciled && (
                <div style={{ ...styles.itemMeta, marginTop: 8 }} data-testid="reconciliation">
                  {plural(receiptSummary.matched, 'receipt line')} matched
                  {receiptSummary.priceChanges > 0 && ` · ${plural(receiptSummary.priceChanges, 'price')} changed`}
                  {receiptSummary.quantityChanges > 0 && ` · ${plural(receiptSummary.quantityChanges, 'quantity')} changed`}
                  {receiptSummary.substitutionsConfirmed > 0 && ` · ${plural(receiptSummary.substitutionsConfirmed, 'substitution')} confirmed`}
                </div>
              )}
              <div style={{ ...styles.toolbar, marginBottom: 0 }}>
                {!isReconciled && (
                  <a
                    href={`/scan-receipts.html?list=${encodeURIComponent(activeList.id)}`}
                    style={{ ...styles.editBtn, textDecoration: 'none' }}
                    data-testid="add-receipt"
                  >
                    Add receipt to verify this trip
                  </a>
                )}
                <button type="button" style={styles.secondaryBtn} onClick={handleReopenTrip} disabled={completing}>
                  Reopen trip
                </button>
              </div>
            </div>
          )}

          <div style={styles.footer}>
            {!isCompleted && !editingPlan && items.length > 0 && (
              <button
                type="button"
                style={{ ...styles.completeBtn, width: '100%' }}
                disabled={completing}
                onClick={() => setReviewOpen(true)}
                data-testid="review-trip"
              >
                {completing ? 'Finishing…' : 'Review & finish'}
              </button>
            )}
            {isCompleted && <div style={styles.summary}>Shopping trip completed {formatShoppingDateTime(activityModel.completed_at)}.</div>}
            <button type="button" style={{ ...styles.secondaryBtn, width: '100%', marginTop: 8 }} onClick={() => window.print()}>
              Print list
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
